import { faLock, faMoneyBillWave, faPlus, faUser, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useEntryFormContext } from 'context/EntryFormContext';
import { formatDate } from 'helpers/formatDate';
import { SUBSCRIPTION_TYPES, SUBSCRIPTION_TYPES_ICONS, SUBSCRIPTION_TYPES_UA } from 'constants/subsctiptionTypes';
import { Tooltip } from 'components/Tooltip';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import styles from 'pages/Schedule/CreateEntry/CreateEntry.scss';

const ClientUtils = {
  findClientByFullName: (clients, fullName) => {
    if (!fullName) return null;
    return clients.find(({ surname, name }) => `${surname} ${name}` === fullName);
  },

  getActiveSubscription: (client) => {
    if (!client?.subscriptions) return null;

    return client.subscriptions.find(
      (sub) =>
        sub.status === 'active' &&
        (sub.type === SUBSCRIPTION_TYPES.SUBSCRIPTION || sub.type === SUBSCRIPTION_TYPES.MEMBERSHIP)
    );
  },
};

export const ClientFields = () => {
  const { form, entryTypeIndex, allClients, selectedGroup } = useEntryFormContext();

  const clientSuggestions = useMemo(() => allClients.map(({ surname, name }) => `${surname} ${name}`), [allClients]);
  const hasTrainer = useMemo(() => form.values.coaches?.some((coach) => coach.coach?.trim()), [form.values.coaches]);
  const isClubEntry = useMemo(() => selectedGroup?.isClub || false, [selectedGroup]);

  const updateClientEntry = useCallback(
    (index, updateFn) => {
      const updatedClients = [...form.values.clients];
      updatedClients[index] = updateFn(updatedClients[index]);
      form.setFieldValue('clients', updatedClients);
    },
    [form]
  );

  const handleClientChange = useCallback(
    (event, index) => {
      const { value: fullName } = event.target;

      const client = ClientUtils.findClientByFullName(allClients, fullName);

      updateClientEntry(index, () => ({
        fullName,
        price: null,
        subscription: ClientUtils.getActiveSubscription(client),
      }));
    },
    [allClients, hasTrainer, isClubEntry, entryTypeIndex, updateClientEntry]
  );

  const handlePriceChange = useCallback(
    (event, index) => {
      const { value } = event.target;
      const isValidPrice = /^\d*$/.test(value.trim());

      updateClientEntry(index, (client) => ({
        ...client,
        price: isValidPrice && value ? value : null,
      }));
    },
    [updateClientEntry]
  );

  const addClientField = useCallback(() => {
    const newClients = [
      ...form.values.clients,
      {
        fullName: '',
        price: null,
        subscription: null,
      },
    ];
    form.setFieldValue('clients', newClients);
  }, [form]);

  const removeClientField = useCallback(
    (index) => {
      const updatedClients = form.values.clients.filter((_, i) => i !== index);
      form.setFieldValue('clients', updatedClients);
    },
    [form]
  );

  const addPaymentField = useCallback(
    (index) => {
      updateClientEntry(index, (client) => ({
        ...client,
        price: '',
      }));
    },
    [updateClientEntry]
  );

  const removePaymentField = useCallback(
    (index) => {
      updateClientEntry(index, (client) => ({
        ...client,
        price: null,
      }));
    },
    [updateClientEntry]
  );

  const clients = form.values.clients || [];
  const error = form.touched.clients && form.errors.clients;

  return (
    <div className={cn(styles.personFields, styles.clients)}>
      <div className={cn(styles.header, styles.main)}>
        <div className={styles.label}>
          <div className={styles.icon}>
            <FontAwesomeIcon icon={faUser} />
          </div>
          Клієнти
        </div>
        <Button className={styles.actionBtn} onClick={addClientField} noStyle>
          <FontAwesomeIcon icon={faPlus} /> Додати клієнта
        </Button>
      </div>

      <div className={styles.list}>
        {clients.map((clientEntry, index) => {
          const client = ClientUtils.findClientByFullName(allClients, clientEntry.fullName);
          const activeSubscription = ClientUtils.getActiveSubscription(client);
          const canRemove = index > 0;
          const hasPaymentField = clientEntry.price !== null;
          const canAddPayment = !hasPaymentField && client;

          return (
            <div className={styles.person} key={index}>
              <div className={styles.header}>
                <div className={styles.label}>
                  <span className={styles.icon}>{index + 1}</span>
                  Клієнт {index + 1}
                </div>

                <div className={styles.buttons}>
                  {canAddPayment && (
                    <Button className={styles.actionBtn} onClick={() => addPaymentField(index)} noStyle>
                      <FontAwesomeIcon icon={faPlus} /> Оплата
                    </Button>
                  )}
                  {canRemove && (
                    <Button
                      className={cn(styles.actionBtn, styles.deleteBtn)}
                      onClick={() => removeClientField(index)}
                      noStyle
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </Button>
                  )}
                </div>
              </div>

              <div className={styles.inputField}>
                {activeSubscription && (
                  <div className={styles.subscription}>
                    <FontAwesomeIcon
                      className={styles.subIcon}
                      icon={SUBSCRIPTION_TYPES_ICONS[activeSubscription.type]}
                    />
                    <Tooltip
                      className={styles.subTooltip}
                      text={`${SUBSCRIPTION_TYPES_UA[activeSubscription.type]} до ${formatDate(
                        activeSubscription.endDate,
                        'UA',
                        true
                      )}`}
                    />
                  </div>
                )}
                <Input
                  className={styles.input}
                  value={clientEntry.fullName}
                  name={`clients.${index}.fullName`}
                  placeholder='Введіть клієнта'
                  onChange={(e) => handleClientChange(e, index)}
                  suggestions={clientSuggestions}
                  icon={clientEntry.locked ? faLock : null}
                  readonly={clientEntry.locked}
                />
              </div>

              {hasPaymentField && (
                <div className={styles.paymentBlock}>
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  <Input
                    className={styles.paymentInput}
                    value={clientEntry.price || ''}
                    name={`clients.${index}.price`}
                    placeholder='Сума'
                    onChange={(e) => handlePriceChange(e, index)}
                    invalid={!!error}
                    errorText={error}
                    readonly={clientEntry.locked}
                  />
                  {!clientEntry.locked && (
                    <button
                      type='button'
                      className={cn(styles.actionBtn, styles.deleteBtn)}
                      onClick={() => removePaymentField(index)}
                      aria-label='Прибрати оплату'
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};
